import { CaretRightOutlined } from "@ant-design/icons";
import { Button, Card, Col, Collapse, Row, Tag, Typography } from "antd";
import { useEffect } from "react";
import { useIsMobileView } from "../../../hooks/ui-hook";
import styles from "./CallDashboardLandingPage.module.scss";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const CallDashboardLandingPage = () => {
  const isMobile = useIsMobileView();
  const faqData = [
    {
      key: "1",
      question: "Can I customize how it sounds and what it says?",
      answer:
        "Absolutely. We'll build it with your chosen voice, your practice preferences, and your workflows. Every call sounds exactly like your team would handle it. Choose from 10+ voices and we configure everything to match your brand.",
    },
    {
      key: "2",
      question: "How quickly can this be live?",
      answer:
        "Fill out the form above to book your implementation session. We'll have your AI receptionist custom-built and answering calls within 48 hours. No IT team needed, no complex setup. We handle everything.",
    },
    {
      key: "3",
      question: "What does this actually cost?",
      answer:
        "Plans start at $400/month for 500 minutes of call time each month (approximately 250 calls). Higher volume practices get custom pricing. That's less than a quarter of what you'd pay for a part-time receptionist for 24/7 coverage.",
    },
    {
      key: "4",
      question: "What happens when the AI can't handle a call?",
      answer:
        "We redirect the call directly to your team with full context from the conversation. Your staff gets a summary of what was discussed, so they can pick up right where the AI left off.",
    },
    {
      key: "5",
      question: "How do I know what's happening with calls?",
      answer:
        "Your calls dashboard shows every call with AI-generated summaries, actionable next steps, patient info collected, smart tags for each call type, and full transcripts you can review anytime. See exactly what patients are asking for and spot trends in real-time.",
    },
  ];

  const handleBookASession = () => {
    window.open("https://calendly.com/sid-getnudgeai/nudge-demo-ai-receptionist", "_blank");
  };

  useEffect(() => {
    const tallyWidgetSrc = "https://tally.so/widgets/embed.js";

    const onLoadOrError = () => {
      // When Tally script is available, ensure embeds are initialized; otherwise, set src from data attribute
      // @ts-ignore - Tally is injected globally by their script
      if (typeof window !== "undefined" && (window as any).Tally) {
        // @ts-ignore
        (window as any).Tally.loadEmbeds();
      } else {
        document.querySelectorAll("iframe[data-tally-src]:not([src])").forEach((el) => {
          const iframe = el as HTMLIFrameElement;
          iframe.src = iframe.dataset.tallySrc || "";
        });
      }
    };

    // Inject Tally script if not already present
    if (typeof window !== "undefined") {
      // @ts-ignore
      if ((window as any).Tally) {
        onLoadOrError();
      } else if (!document.querySelector(`script[src="${tallyWidgetSrc}"]`)) {
        const s = document.createElement("script");
        s.src = tallyWidgetSrc;
        s.onload = onLoadOrError;
        s.onerror = onLoadOrError;
        document.body.appendChild(s);
      } else {
        onLoadOrError();
      }
    }

    const handleMessage = (e: MessageEvent) => {
      // Tally posts string messages like 'Tally.FormSubmitted'
      if (e?.data && String(e.data).includes("Tally.FormSubmitted")) {
        handleBookASession();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <div className={styles.landingPage}>
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(180deg, #E7E8FF 0%, #FFF8F2 50%, #FFF 100%)",
        }}
      >
        <section className={styles.landingPage__hero}>
          <div className={styles.landingPage__container}>
            <Row gutter={isMobile ? [24, 24] : [48, 48]} align="middle">
              <Col xs={24} lg={14}>
                <div className={styles.landingPage__hero_content}>
                  <Tag className={styles.landingPage__price_tag}>
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M7 0C5.80653 0 4.66193 0.474106 3.81802 1.31802C2.97411 2.16193 2.5 3.30653 2.5 4.5V8H2C1.46957 8 0.960859 8.21071 0.585786 8.58579C0.210714 8.96086 0 9.46957 0 10V16C0 16.5304 0.210714 17.0391 0.585786 17.4142C0.960859 17.7893 1.46957 18 2 18H12C12.5304 18 13.0391 17.7893 13.4142 17.4142C13.7893 17.0391 14 16.5304 14 16V10C14 9.46957 13.7893 8.96086 13.4142 8.58579C13.0391 8.21071 12.5304 8 12 8H11.5V4.5C11.5 3.30653 11.0259 2.16193 10.182 1.31802C9.33807 0.474106 8.19347 0 7 0ZM10 8V4.5C10 3.70435 9.68393 2.94129 9.12132 2.37868C8.55871 1.81607 7.79565 1.5 7 1.5C6.20435 1.5 5.44129 1.81607 4.87868 2.37868C4.31607 2.94129 4 3.70435 4 4.5V8H10Z"
                        fill="url(#paint0_linear_5274_11918)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_5274_11918"
                          x1="7"
                          y1="0"
                          x2="7"
                          y2="18"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#6366F1" />
                          <stop offset="1" stop-color="#9A9CFF" />
                        </linearGradient>
                      </defs>
                    </svg>
                    24/7 AI Receptionist
                  </Tag>

                  <Title level={1} className={styles.landingPage__hero_title}>
                    Our AI Receptionist: It Handles Calls. You Handle Care.
                  </Title>

                  <div className={styles.landingPage__features_list}>
                    <div className={styles.landingPage__feature_item}>
                      <Text className={styles.landingPage__feature_text}>
                        <strong style={{ fontWeight: "700" }}>35% More New Patient Bookings:</strong> AI books while
                        they’re ready, not only when you’re available.
                      </Text>
                    </div>
                    <div className={styles.landingPage__feature_item}>
                      <Text className={styles.landingPage__feature_text}>
                        <strong style={{ fontWeight: "700" }}>80% Fewer Missed Calls:</strong> No wait times, available
                        after-hours and weekends.
                      </Text>
                    </div>
                    <div className={styles.landingPage__feature_item}>
                      <Text className={styles.landingPage__feature_text}>
                        <strong style={{ fontWeight: "700" }}>70% of Calls Handled Without Staff:</strong> AI captures
                        scheduling, intake, prescriptions, and FAQs.
                      </Text>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={24} lg={10}>
                <Card className={styles.landingPage__hero_form_card}>
                  <div className={styles.landingPage__demo_form_content}>
                    <iframe
                      data-tally-src="https://tally.so/embed/wzPWLa?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
                      loading="lazy"
                      width="100%"
                      height="520"
                      frameBorder={0}
                      marginHeight={0}
                      marginWidth={0}
                      title="Nudge AI Receptionist - Demo Form"
                      style={{ border: "0", borderRadius: 12, background: "transparent" }}
                    ></iframe>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </section>
      </div>

      {/* Video/Demo Section */}
      <section className={styles.landingPage__demo}>
        <div className={styles.landingPage__video_container}>
          <iframe
            width="900"
            height="450"
            src="https://www.youtube.com/embed/BP1IJcnScyQ?si=B0nsbk2W2CmMbaD5"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className={styles.landingPage__youtube_video}
          ></iframe>
        </div>
        <BookADemoButton handleBookASession={handleBookASession} />
      </section>

      {/* FAQ Section */}
      <section className={styles.landingPage__faq}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__faq_header}>
            <Tag className={styles.landingPage__faq_tag}>FAQs</Tag>
            <Title level={2} className={styles.landingPage__faq_title}>
              Curious? Learn more.
            </Title>
          </div>

          <div className={styles.landingPage__faq_content}>
            <Collapse
              ghost
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              className={styles.landingPage__faq_collapse}
            >
              {faqData.map((faq) => (
                <Panel
                  header={<Text className={styles.landingPage__faq_question}>{faq.question}</Text>}
                  key={faq.key}
                  className={styles.landingPage__faq_panel}
                >
                  <Paragraph className={styles.landingPage__faq_answer}>{faq.answer}</Paragraph>
                </Panel>
              ))}
            </Collapse>
            <BookADemoButton handleBookASession={handleBookASession} />
          </div>
        </div>
      </section>
    </div>
  );
};

const BookADemoButton = ({ handleBookASession }: { handleBookASession: () => void }) => {
  return (
    <Button onClick={handleBookASession} type="primary" size="large" className={styles.book_a_demo_button}>
      Book Demo
    </Button>
  );
};

export default CallDashboardLandingPage;
